import { api } from '../config/api';

export interface PocketSmithTransaction {
  id: number;
  pocketsmith_id: number;
  date: string;
  amount: number;
  payee: string;
  original_payee: string;
  category_name: string;
  category_override: string | null;
  user_notes: string | null;
  note: string;
  is_transfer: boolean;
  status: string;
  closing_balance: number | null;
  account_name: string;
  property_id: number | null;
  expense_id: number | null;
  labels: string;
  created_at: string;
}

export interface PocketSmithAccount {
  id: number;
  pocketsmith_account_id: number;
  transaction_account_id: number;
  name: string;
  institution_name: string;
  account_type: string;
  balance: number;
  currency: string;
  account_group: string;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  created: number;
  updated: number;
  pages_fetched: number;
  rules_applied?: number;
}

export interface TransactionsResponse {
  success: boolean;
  data: PocketSmithTransaction[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface SummaryResponse {
  success: boolean;
  total_transactions: number;
  unmatched: number;
  matched: number;
  by_account: { account_name: string; count: number; total_amount: number }[];
  date_range: { start: string | null; end: string | null };
  last_sync: string | null;
}

export interface MatchRule {
  id: number;
  payee_contains: string | null;
  account_name_contains: string | null;
  category_contains: string | null;
  amount_min: number | null;
  amount_max: number | null;
  note_contains: string | null;
  property_id: number;
  label: string;
  match_count: number;
  is_active: boolean;
  priority: number;
}

export interface AccountGroup {
  key: string;
  label: string;
  color: string;
  subtotal: number;
  account_count: number;
  accounts: PocketSmithAccount[];
}

export interface GroupedAccountsResponse {
  success: boolean;
  groups: AccountGroup[];
  grand_total: number;
}

export interface CategoryBreakdown {
  category: string;
  total: number;
  count: number;
}

export interface MonthlyTrend {
  month: string;
  income: number;
  expenses: number;
  net: number;
  count: number;
}

export interface RulePreviewResult {
  success: boolean;
  match_count: number;
  samples: {
    id: number;
    date: string;
    payee: string;
    amount: number;
    account_name: string;
    category_name: string;
  category_override: string | null;
  user_notes: string | null;
  }[];
}

export interface DuplicateGroup {
  date: string;
  payee: string;
  amount: number;
  count: number;
  transactions: {
    id: number;
    pocketsmith_id: number;
    date: string;
    payee: string;
    amount: number;
    account_name: string;
    property_id: number | null;
  }[];
}

export interface RecurringPattern {
  payee: string;
  account_name: string;
  frequency: string;
  avg_interval_days: number;
  avg_amount: number;
  transaction_count: number;
  last_date: string;
  next_expected: string;
  is_overdue: boolean;
}

export interface MatchSuggestion {
  payee: string;
  suggested_property_id: number;
  confidence: number;
  unmatched_count: number;
}

export const checkStatus = async () => {
  const response = await api.get('/pocketsmith/status');
  return response.data;
};

export const getAccounts = async () => {
  const response = await api.get('/pocketsmith/accounts');
  return response.data;
};

export const getGroupedAccounts = async (): Promise<GroupedAccountsResponse> => {
  const response = await api.get('/pocketsmith/accounts/grouped');
  return response.data;
};

export const updateAccountGroup = async (
  accountId: number,
  accountGroup: string
): Promise<{ success: boolean }> => {
  const response = await api.put(`/pocketsmith/accounts/${accountId}/group`, {
    account_group: accountGroup,
  });
  return response.data;
};

export const syncTransactions = async (params: {
  start_date?: string;
  end_date?: string;
  account_id?: number;
}) => {
  const response = await api.post('/pocketsmith/sync', params);
  return response.data as SyncResult;
};

export const getTransactions = async (params: {
  start_date?: string;
  end_date?: string;
  account_name?: string;
  account_group?: string;
  payee?: string;
  search?: string;
  unmatched_only?: boolean;
  exclude_transfers?: boolean;
  sort_by?: string;
  sort_dir?: string;
  page?: number;
  per_page?: number;
}) => {
  const response = await api.get('/pocketsmith/transactions', { params });
  return response.data as TransactionsResponse;
};

export const matchTransaction = async (
  transactionId: number,
  data: { property_id?: number; expense_id?: number }
) => {
  const response = await api.put(`/pocketsmith/transactions/${transactionId}/match`, data);
  return response.data;
};

export const getSummary = async () => {
  const response = await api.get('/pocketsmith/summary');
  return response.data as SummaryResponse;
};

// --- Analytics ---

export const getCategoryBreakdown = async (params: {
  start_date?: string;
  end_date?: string;
  account_group?: string;
  exclude_transfers?: boolean;
}): Promise<{ success: boolean; data: CategoryBreakdown[] }> => {
  const response = await api.get('/pocketsmith/analytics/by-category', { params });
  return response.data;
};

export const getMonthlyTrend = async (params: {
  months?: number;
  account_group?: string;
  exclude_transfers?: boolean;
}): Promise<{ success: boolean; data: MonthlyTrend[] }> => {
  const response = await api.get('/pocketsmith/analytics/monthly-trend', { params });
  return response.data;
};

export const getRecurringPatterns = async (params?: {
  account_group?: string;
  min_occurrences?: number;
}): Promise<{ success: boolean; count: number; data: RecurringPattern[] }> => {
  const response = await api.get('/pocketsmith/analytics/recurring', { params });
  return response.data;
};

// --- Bulk Operations ---

export const bulkMatchTransactions = async (
  transactionIds: number[],
  propertyId: number
): Promise<{ success: boolean; updated: number }> => {
  const response = await api.post('/pocketsmith/transactions/bulk-match', {
    transaction_ids: transactionIds,
    property_id: propertyId,
  });
  return response.data;
};

// --- Smart Features ---

export const previewRule = async (criteria: {
  payee_contains?: string;
  account_name_contains?: string;
  category_contains?: string;
}): Promise<RulePreviewResult> => {
  const response = await api.post('/pocketsmith/rules/preview', criteria);
  return response.data;
};

export const getDuplicates = async (): Promise<{
  success: boolean;
  duplicate_groups: number;
  total_duplicates: number;
  groups: DuplicateGroup[];
}> => {
  const response = await api.get('/pocketsmith/transactions/duplicates');
  return response.data;
};

export const getMatchSuggestions = async (): Promise<{
  success: boolean;
  count: number;
  data: MatchSuggestion[];
}> => {
  const response = await api.get('/pocketsmith/transactions/suggestions');
  return response.data;
};

// --- Match Rules ---

export const getRules = async () => {
  const response = await api.get('/pocketsmith/rules');
  return response.data as { success: boolean; data: MatchRule[] };
};

export const createRule = async (data: {
  payee_contains?: string;
  account_name_contains?: string;
  category_contains?: string;
  amount_min?: number;
  amount_max?: number;
  note_contains?: string;
  property_id: number;
  label?: string;
}) => {
  const response = await api.post('/pocketsmith/rules', data);
  return response.data;
};

export const createRuleFromTransaction = async (transactionId: number) => {
  const response = await api.post(`/pocketsmith/rules/from-transaction/${transactionId}`);
  return response.data;
};

export const updateRule = async (
  ruleId: number,
  data: {
    payee_contains?: string | null;
    account_name_contains?: string | null;
    category_contains?: string | null;
    amount_min?: number | null;
    amount_max?: number | null;
    note_contains?: string | null;
    property_id?: number;
    label?: string;
    is_active?: boolean;
    priority?: number;
  }
) => {
  const response = await api.put(`/pocketsmith/rules/${ruleId}`, data);
  return response.data;
};

export const deleteRule = async (ruleId: number) => {
  const response = await api.delete(`/pocketsmith/rules/${ruleId}`);
  return response.data;
};

export const applyRules = async () => {
  const response = await api.post('/pocketsmith/apply-rules');
  return response.data;
};

// --- Property Cash Flow (Mod 12) ---

export interface PropertyCashFlow {
  success: boolean;
  property_id: number;
  transaction_count: number;
  total_income: number;
  total_expenses: number;
  net: number;
  transactions: {
    id: number;
    date: string;
    payee: string;
    amount: number;
    category_name: string;
  category_override: string | null;
  user_notes: string | null;
    account_name: string;
    is_transfer: boolean;
  }[];
  monthly_summary: MonthlyTrend[];
}

export const getPropertyCashFlow = async (
  propertyId: number,
  months?: number
): Promise<PropertyCashFlow> => {
  const response = await api.get(`/pocketsmith/transactions/by-property/${propertyId}`, {
    params: months ? { months } : undefined,
  });
  return response.data;
};

// --- Account Balance History (Mod 18) ---

export interface BalanceHistory {
  success: boolean;
  account_id: number;
  account_name: string;
  current_balance: number;
  data_points: { date: string; balance: number }[];
  change: number;
  change_pct: number;
  period_days: number;
}

export const getBalanceHistory = async (
  accountId: number,
  days?: number
): Promise<BalanceHistory> => {
  const response = await api.get(`/pocketsmith/accounts/${accountId}/balance-history`, {
    params: days ? { days } : undefined,
  });
  return response.data;
};

// --- Subscription Reconciliation (Mod 20) ---

export interface SubscriptionCheck {
  subscription_id: number;
  site_name: string;
  category: string;
  billing_cycle: string;
  expected_monthly: number;
  avg_actual: number;
  last_charge_date: string | null;
  last_charge_amount: number;
  matched_count: number;
  status: 'matched' | 'price_changed' | 'amount_mismatch' | 'overdue' | 'no_match';
  days_since_last: number | null;
}

export const getSubscriptionCheck = async (
  months?: number
): Promise<{ success: boolean; count: number; matched: number; issues: number; data: SubscriptionCheck[] }> => {
  const response = await api.get('/pocketsmith/analytics/subscription-check', {
    params: months ? { months } : undefined,
  });
  return response.data;
};

// --- Property Spending Analytics ---

export interface PropertyBreakdown {
  property_id: number;
  property_name: string;
  income: number;
  expenses: number;
  net: number;
  count: number;
}

export const getPropertyBreakdown = async (params: {
  months?: number;
  account_group?: string;
  exclude_transfers?: boolean;
}): Promise<{ success: boolean; data: PropertyBreakdown[] }> => {
  const response = await api.get('/pocketsmith/analytics/by-property', { params });
  return response.data;
};
